import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
    Search, RefreshCw, Download, Printer, Archive, Mail, AlertTriangle,
    ChevronDown, ChevronUp, X, CheckCircle2, Send, ShieldQuestion,
} from "lucide-react";
import { api, BACKEND_URL, formatApiError } from "@/lib/api";

const STATUS_ORDER = [
    "new", "under_review", "contacted", "interview_scheduled",
    "interview_completed", "approved", "waiting_placement", "active",
    "on_hold", "declined", "completed", "archived",
];

const STATUS_STYLES = {
    new: "bg-blue-50 text-blue-700",
    under_review: "bg-indigo-50 text-indigo-700",
    contacted: "bg-cyan-50 text-cyan-700",
    interview_scheduled: "bg-amber-50 text-amber-700",
    interview_completed: "bg-yellow-50 text-yellow-800",
    approved: "bg-emerald-50 text-emerald-700",
    waiting_placement: "bg-teal-50 text-teal-700",
    active: "bg-green-100 text-green-800",
    on_hold: "bg-orange-50 text-orange-700",
    declined: "bg-red-50 text-red-700",
    completed: "bg-slate-100 text-slate-700",
    archived: "bg-gray-100 text-gray-600",
};

function fmt(dt) {
    if (!dt) return "—";
    try { return new Date(dt).toLocaleString(); } catch { return dt; }
}

export default function AdminVolunteers() {
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [summary, setSummary] = useState(null);
    const [q, setQ] = useState("");
    const [status, setStatus] = useState("");
    const [duplicateOnly, setDuplicateOnly] = useState(false);
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [sort, setSort] = useState("created_at");
    const [order, setOrder] = useState("desc");
    const [selectedId, setSelectedId] = useState(null);
    const [selected, setSelected] = useState(null);
    const [loading, setLoading] = useState(true);
    const [detailLoading, setDetailLoading] = useState(false);

    const statusLabels = useMemo(() => {
        const map = {};
        (summary?.statuses || []).forEach((s) => { map[s.key] = s.label; });
        STATUS_ORDER.forEach((k) => { if (!map[k]) map[k] = k.replace(/_/g, " "); });
        return map;
    }, [summary]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (q) params.set("q", q);
            if (status) params.set("status", status);
            if (duplicateOnly) params.set("duplicate_only", "true");
            if (fromDate) params.set("from_date", fromDate);
            if (toDate) params.set("to_date", toDate);
            if (sort) params.set("sort", sort);
            if (order) params.set("order", order);
            const { data } = await api.get(`/admin/volunteers?${params.toString()}`);
            setItems(data.items || []);
            setTotal(data.total || 0);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    }, [q, status, duplicateOnly, fromDate, toDate, sort, order]);

    const loadSummary = useCallback(async () => {
        try {
            const { data } = await api.get("/admin/volunteers/summary");
            setSummary(data);
        } catch (e) {
            /* soft-fail */
        }
    }, []);

    useEffect(() => { load(); }, [load]);
    useEffect(() => { loadSummary(); }, [loadSummary]);

    const openDetail = useCallback(async (id) => {
        setSelectedId(id);
        setDetailLoading(true);
        try {
            const { data } = await api.get(`/admin/volunteers/${id}`);
            setSelected(data);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally { setDetailLoading(false); }
    }, []);

    const refreshBoth = useCallback(async () => {
        await Promise.all([load(), loadSummary()]);
        if (selectedId) await openDetail(selectedId);
    }, [load, loadSummary, openDetail, selectedId]);

    async function changeStatus(newStatus, note) {
        if (!selectedId) return;
        try {
            await api.patch(`/admin/volunteers/${selectedId}`, { status: newStatus, note: note || "" });
            toast.success(`Status updated to ${statusLabels[newStatus] || newStatus}`);
            await refreshBoth();
        } catch (e) { toast.error(formatApiError(e)); }
    }

    async function saveNotes(nextNotes) {
        if (!selectedId) return;
        try {
            await api.patch(`/admin/volunteers/${selectedId}`, { admin_notes: nextNotes });
            toast.success("Notes saved");
            await refreshBoth();
        } catch (e) { toast.error(formatApiError(e)); }
    }

    async function archiveNow() {
        if (!selectedId) return;
        if (!window.confirm("Archive this application? It will be retained in the database and remain accessible.")) return;
        try {
            await api.post(`/admin/volunteers/${selectedId}/archive`);
            toast.success("Application archived");
            await refreshBoth();
        } catch (e) { toast.error(formatApiError(e)); }
    }

    async function resendAck() {
        if (!selectedId) return;
        try {
            const { data } = await api.post(`/admin/volunteers/${selectedId}/resend-acknowledgement`);
            if (data.sent) toast.success("Acknowledgement sent");
            else if (data.queued) toast.info("Acknowledgement queued (Resend not configured)");
            else if (data.skipped) toast.info("No applicant email — nothing to send");
            else toast.error(data.error || "Delivery failed — queued for retry");
            await refreshBoth();
        } catch (e) { toast.error(formatApiError(e)); }
    }

    async function dismissDup() {
        if (!selectedId) return;
        try {
            await api.post(`/admin/volunteers/${selectedId}/dismiss-duplicate`);
            toast.success("Duplicate flag dismissed");
            await refreshBoth();
        } catch (e) { toast.error(formatApiError(e)); }
    }

    function printCurrent() {
        window.print();
    }

    function exportUrl(kind) {
        const params = new URLSearchParams();
        if (q) params.set("q", q);
        if (status) params.set("status", status);
        if (duplicateOnly) params.set("duplicate_only", "true");
        if (fromDate) params.set("from_date", fromDate);
        if (toDate) params.set("to_date", toDate);
        return `${BACKEND_URL}/api/admin/volunteers/export.${kind}?${params.toString()}`;
    }

    return (
        <div data-testid="admin-volunteers">
            <style>{`
                @media print {
                    aside.no-print, .no-print { display: none !important; }
                    body { background: #fff; }
                    #volunteer-print-area { padding: 8mm; }
                }
            `}</style>
            <div className="flex justify-between items-end flex-wrap gap-3 no-print">
                <div>
                    <div className="gsf-eyebrow">Volunteer Applications</div>
                    <h1 className="mt-2 font-serif text-3xl text-[color:var(--gsf-green-700)] font-bold" data-testid="volunteers-title">
                        Volunteer Applications ({total})
                    </h1>
                    {summary && (
                        <p className="text-xs text-gray-500 mt-1" data-testid="volunteers-meta">
                            Storage: <code>form_submissions</code> · Notifications: <code>{summary.admin_notification_email}</code>
                            {summary.email_provider_configured
                                ? <span className="text-green-700"> · Email provider active</span>
                                : <span className="text-amber-700"> · Email provider not yet active (queued)</span>}
                            {summary.possible_duplicates > 0 && (
                                <span className="text-amber-700"> · {summary.possible_duplicates} possible duplicate(s)</span>
                            )}
                        </p>
                    )}
                </div>
                <div className="flex gap-2 flex-wrap">
                    <a href={exportUrl("csv")} className="gsf-btn-outline text-sm" data-testid="export-csv">
                        <Download size={14} /> CSV
                    </a>
                    <a href={exportUrl("xlsx")} className="gsf-btn-outline text-sm" data-testid="export-xlsx">
                        <Download size={14} /> Excel
                    </a>
                    <button onClick={refreshBoth} className="gsf-btn-outline text-sm" data-testid="refresh-btn">
                        <RefreshCw size={14} /> Refresh
                    </button>
                </div>
            </div>

            {summary && (
                <div className="mt-6 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2 no-print" data-testid="status-counts">
                    {STATUS_ORDER.map((k) => {
                        const c = summary.counts_by_status?.[k] || 0;
                        return (
                            <button key={k} onClick={() => setStatus(status === k ? "" : k)}
                                data-testid={`count-${k}`}
                                className={`text-left rounded-xl border p-3 hover:border-[color:var(--gsf-green-600)] transition-colors ${status === k ? "border-[color:var(--gsf-green-700)] bg-[color:var(--gsf-soft-green)]" : "border-gray-200 bg-white"}`}>
                                <div className="text-xl font-serif font-bold text-[color:var(--gsf-green-700)]">{c}</div>
                                <div className="text-[10px] uppercase tracking-widest text-gray-500">{statusLabels[k]}</div>
                            </button>
                        );
                    })}
                </div>
            )}

            <div className="mt-6 flex flex-wrap gap-3 items-end no-print" data-testid="volunteer-filters">
                <div className="flex-1 min-w-[240px]">
                    <label className="text-xs text-gray-500">Search (name / email / phone / skills)</label>
                    <div className="relative">
                        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…"
                            data-testid="volunteer-search"
                            className="w-full rounded-lg border border-gray-200 pl-9 pr-3 py-2 text-sm bg-white" />
                    </div>
                </div>
                <div>
                    <label className="text-xs text-gray-500">Status</label>
                    <select value={status} onChange={(e) => setStatus(e.target.value)}
                        data-testid="filter-status"
                        className="rounded-lg border border-gray-200 px-3 py-2 text-sm bg-white block">
                        <option value="">All statuses</option>
                        {STATUS_ORDER.map((s) => <option key={s} value={s}>{statusLabels[s]}</option>)}
                    </select>
                </div>
                <div>
                    <label className="text-xs text-gray-500">From date</label>
                    <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)}
                        data-testid="filter-from-date"
                        className="rounded-lg border border-gray-200 px-3 py-2 text-sm bg-white block" />
                </div>
                <div>
                    <label className="text-xs text-gray-500">To date</label>
                    <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)}
                        data-testid="filter-to-date"
                        className="rounded-lg border border-gray-200 px-3 py-2 text-sm bg-white block" />
                </div>
                <div>
                    <label className="text-xs text-gray-500">Sort</label>
                    <div className="flex gap-1">
                        <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="sort-field"
                            className="rounded-lg border border-gray-200 px-2 py-2 text-sm bg-white">
                            <option value="created_at">Date</option>
                            <option value="full_name">Name</option>
                            <option value="status">Status</option>
                        </select>
                        <button onClick={() => setOrder(order === "desc" ? "asc" : "desc")}
                            data-testid="sort-order"
                            className="rounded-lg border border-gray-200 px-2 py-2 text-sm bg-white">
                            {order === "desc" ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
                        </button>
                    </div>
                </div>
                <label className="flex items-center gap-2 text-xs text-gray-600 mt-4" data-testid="duplicate-filter-wrap">
                    <input type="checkbox" checked={duplicateOnly} onChange={(e) => setDuplicateOnly(e.target.checked)}
                        data-testid="filter-duplicates" />
                    Possible duplicates only
                </label>
            </div>

            <div className="mt-6 grid grid-cols-1 xl:grid-cols-5 gap-6">
                <div className="xl:col-span-3 gsf-card overflow-hidden no-print">
                    <table className="w-full text-sm">
                        <thead className="bg-[#f8faf7] text-left">
                            <tr>
                                <th className="p-3">Name</th>
                                <th className="p-3">Email</th>
                                <th className="p-3">Phone</th>
                                <th className="p-3">Received</th>
                                <th className="p-3">Status</th>
                                <th className="p-3">Flags</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan={6} className="p-6 text-gray-500 text-center">Loading…</td></tr>
                            ) : items.length === 0 ? (
                                <tr><td colSpan={6} className="p-6 text-gray-500 text-center" data-testid="no-volunteers">No volunteer applications match your filters.</td></tr>
                            ) : items.map((it) => (
                                <tr key={it.id} onClick={() => openDetail(it.id)}
                                    data-testid={`volunteer-row-${it.id}`}
                                    className={`border-t border-gray-100 cursor-pointer hover:bg-[color:var(--gsf-soft-green)]/50 ${selectedId === it.id ? "bg-[color:var(--gsf-soft-green)]" : ""}`}>
                                    <td className="p-3 font-medium">{it.full_name || "—"}</td>
                                    <td className="p-3 text-gray-600 break-all">{it.email || "—"}</td>
                                    <td className="p-3 text-gray-600">{it.phone || "—"}</td>
                                    <td className="p-3 text-gray-500 whitespace-nowrap">{fmt(it.created_at)}</td>
                                    <td className="p-3"><StatusBadge status={it.status} label={statusLabels[it.status]} /></td>
                                    <td className="p-3 space-x-1">
                                        {it.possible_duplicate && (
                                            <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-800" data-testid={`dup-flag-${it.id}`}>
                                                <AlertTriangle size={10} /> Possible duplicate
                                            </span>
                                        )}
                                        {it.acknowledgement_status === "sent" && (
                                            <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-green-50 text-green-700">
                                                <CheckCircle2 size={10} /> Ack sent
                                            </span>
                                        )}
                                        {(it.acknowledgement_status === "queued" || it.acknowledgement_status === "failed") && (
                                            <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-700">
                                                <Send size={10} /> Ack queued
                                            </span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <aside className="xl:col-span-2" id="volunteer-print-area">
                    {selected ? (
                        detailLoading ? <div className="gsf-card p-8 text-center text-gray-500 text-sm">Loading…</div> :
                            <VolunteerDetail
                                v={selected} statusLabels={statusLabels}
                                onStatus={changeStatus} onSaveNotes={saveNotes}
                                onArchive={archiveNow} onResendAck={resendAck}
                                onDismissDup={dismissDup} onPrint={printCurrent}
                                onClose={() => { setSelected(null); setSelectedId(null); }}
                            />
                    ) : (
                        <div className="gsf-card p-8 text-center text-gray-500 text-sm no-print">Select a volunteer application to review details.</div>
                    )}
                </aside>
            </div>
        </div>
    );
}

function StatusBadge({ status, label }) {
    const cls = STATUS_STYLES[status] || "bg-gray-100 text-gray-700";
    return <span className={`inline-block text-[11px] px-2 py-1 rounded-full ${cls}`} data-testid={`status-badge-${status}`}>{label || status}</span>;
}

function VolunteerDetail({ v, statusLabels, onStatus, onSaveNotes, onArchive, onResendAck, onDismissDup, onPrint, onClose }) {
    const [notes, setNotes] = useState(v.admin_notes || "");
    const [note, setNote] = useState("");
    useEffect(() => { setNotes(v.admin_notes || ""); }, [v.id, v.admin_notes]);
    const email = v.email;
    const phoneClean = (v.phone || "").replace(/[^0-9+]/g, "");
    const waNumber = phoneClean.replace(/^\+/, "");

    return (
        <div className="gsf-card p-6 sticky top-6" data-testid="volunteer-detail">
            <div className="flex justify-between items-start no-print">
                <div>
                    <div className="gsf-eyebrow">Volunteer Application</div>
                    <h3 className="mt-1 font-serif text-xl text-[color:var(--gsf-green-700)] font-semibold">{v.full_name || "Unnamed applicant"}</h3>
                    <p className="text-xs text-gray-500 mt-1">Received {fmt(v.created_at)} · ID <code>{v.id}</code></p>
                </div>
                <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-sm" data-testid="detail-close"><X size={16} /></button>
            </div>

            <div className="mt-3 flex flex-wrap gap-2 items-center">
                <StatusBadge status={v.status} label={statusLabels[v.status]} />
                {v.possible_duplicate && (
                    <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-800" data-testid="detail-dup-flag">
                        <AlertTriangle size={10} /> Possible duplicate {v.duplicate_reason ? `(${v.duplicate_reason.replace(/_/g, " ")})` : ""}
                    </span>
                )}
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">Kind: {v.kind_label}</span>
            </div>

            <div className="mt-4 grid grid-cols-3 gap-2 text-sm" data-testid="volunteer-fields">
                <Row label="Full name" value={v.full_name} />
                <Row label="Email" value={v.email} testid="v-email-val" />
                <Row label="Phone" value={v.phone} testid="v-phone-val" />
                <Row label="Area of residence" value={v.area} />
                <Row label="Preferred programme" value={v.preferred_programme} testid="v-programme-val" />
                <Row label="Skills" value={v.skills} testid="v-skills-val" />
                <Row label="Availability" value={v.availability} testid="v-availability-val" />
                <Row label="Other interests / motivation" value={v.other_interests} wide testid="v-other-val" />
                <Row label="Acknowledgement" value={`${v.acknowledgement_status || "pending"}${v.acknowledgement_sent_at ? ` — ${fmt(v.acknowledgement_sent_at)}` : ""}`} />
                {v.acknowledgement_last_error && <Row label="Ack error" value={v.acknowledgement_last_error} />}
            </div>

            <div className="mt-5 border-t border-gray-100 pt-4 no-print">
                <div className="text-xs text-gray-500 mb-2">Change status</div>
                <div className="flex flex-wrap gap-1.5" data-testid="status-buttons">
                    {STATUS_ORDER.map((s) => (
                        <button key={s} onClick={() => onStatus(s, note)}
                            data-testid={`set-status-${s}`}
                            className={`text-[11px] px-3 py-1.5 rounded-full ${v.status === s ? "bg-[color:var(--gsf-green-700)] text-white" : "bg-[color:var(--gsf-soft-green)] text-[color:var(--gsf-green-700)] hover:bg-[color:var(--gsf-green-700)]/20"}`}>
                            {statusLabels[s]}
                        </button>
                    ))}
                </div>
                <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2}
                    placeholder="Optional note attached to this status change…"
                    data-testid="status-change-note"
                    className="mt-3 w-full rounded-lg border border-gray-200 p-2 text-sm bg-white" />
            </div>

            <div className="mt-5 border-t border-gray-100 pt-4 no-print">
                <label className="text-xs text-gray-500">Internal administrator notes (not visible to applicant)</label>
                <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3}
                    data-testid="admin-notes"
                    className="mt-1 w-full rounded-lg border border-gray-200 p-2 text-sm bg-white" />
                <button onClick={() => onSaveNotes(notes)} className="gsf-btn-outline text-xs mt-2" data-testid="save-notes-btn">Save notes</button>
            </div>

            <div className="mt-5 border-t border-gray-100 pt-4 flex flex-wrap gap-2 no-print" data-testid="volunteer-actions">
                <button onClick={onResendAck} className="gsf-btn-outline text-xs" data-testid="resend-ack-btn">
                    <Mail size={12} /> Resend acknowledgement
                </button>
                {v.possible_duplicate && (
                    <button onClick={onDismissDup} className="gsf-btn-outline text-xs" data-testid="dismiss-dup-btn">
                        <ShieldQuestion size={12} /> Dismiss duplicate flag
                    </button>
                )}
                {email && (
                    <a href={`mailto:${email}`} className="gsf-btn-outline text-xs" data-testid="mailto-btn">
                        <Mail size={12} /> Email applicant
                    </a>
                )}
                {waNumber && (
                    <a href={`https://wa.me/${waNumber}`} target="_blank" rel="noreferrer" className="gsf-btn-outline text-xs" data-testid="whatsapp-btn">
                        WhatsApp
                    </a>
                )}
                <button onClick={onPrint} className="gsf-btn-outline text-xs" data-testid="print-btn">
                    <Printer size={12} /> Print
                </button>
                {v.status !== "archived" && (
                    <button onClick={onArchive} className="gsf-btn-outline text-xs text-red-700" data-testid="archive-btn">
                        <Archive size={12} /> Archive
                    </button>
                )}
            </div>

            {(v.status_history || []).length > 0 && (
                <div className="mt-5 border-t border-gray-100 pt-4" data-testid="status-history">
                    <div className="text-xs text-gray-500 mb-2">Status history</div>
                    <ol className="space-y-2 text-xs">
                        {[...(v.status_history || [])].reverse().map((h, i) => (
                            <li key={i} className="border-l-2 border-[color:var(--gsf-gold-500)] pl-3">
                                <div className="font-medium text-[color:var(--gsf-green-700)]">{statusLabels[h.status] || h.status}</div>
                                <div className="text-gray-500">{fmt(h.at)} · {h.actor_email || "system"}</div>
                                {h.note && <div className="text-gray-600 mt-0.5 italic">“{h.note}”</div>}
                            </li>
                        ))}
                    </ol>
                </div>
            )}

            {(v.related_submissions || []).length > 0 && (
                <div className="mt-5 border-t border-gray-100 pt-4" data-testid="related-submissions">
                    <div className="text-xs text-gray-500 mb-2">Linked / possible duplicate records</div>
                    <ul className="space-y-1 text-xs">
                        {v.related_submissions.map((r) => (
                            <li key={r.id} className="flex items-center justify-between gap-2">
                                <span>{r.full_name || "—"} · {r.email || r.phone || r.id}</span>
                                <span className="text-gray-400">{fmt(r.created_at)}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}

function Row({ label, value, wide, testid }) {
    return (
        <>
            <div className="text-gray-500 text-xs col-span-1">{label}</div>
            <div className={`${wide ? "col-span-2" : "col-span-2"} break-words text-sm`} data-testid={testid}>{value || "—"}</div>
        </>
    );
}
